/**
 * ResetPasswordPage.js - 重置密码页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  message,
  Spin,
} from 'antd';
import {
  LockOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { authApi } from '../../api/index';
import { useStore } from '../../contexts/StoreContext';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeName, logoUrl } = useStore();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const token = searchParams.get('token');

  /**
   * 验证重置令牌
   */
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const result = await authApi.validateResetToken(token);
        setTokenValid(result.valid);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  /**
   * 重置密码
   */
  const handleSubmit = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, values.password);
      setResetSuccess(true);
      message.success('密码已重置，请登录');
      // 3 秒后跳转到登录页
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      message.error(err?.response?.data?.message || '重置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
        }}
      >
        <Spin size="large" tip="验证中..." />
      </div>
    );
  }

  if (!token) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
          padding: '24px',
        }}
      >
        <Card
          style={{
            maxWidth: 440,
            textAlign: 'center',
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <SafetyOutlined
            style={{
              fontSize: 56,
              color: COLORS.cinnabar,
              marginBottom: 16,
            }}
          />
          <Title level={4} style={{ color: COLORS.ink }}>无效的请求</Title>
          <Text style={{ color: COLORS.lightInk }}>缺少重置令牌，请从邮件中的链接访问</Text>
          <div style={{ marginTop: 24 }}>
            <Link to="/forgot-password">
              <Button
                type="primary"
                style={{
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                重新获取重置链接
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
          padding: '24px',
        }}
      >
        <Card
          style={{
            maxWidth: 440,
            textAlign: 'center',
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <SafetyOutlined
            style={{
              fontSize: 56,
              color: COLORS.cinnabar,
              marginBottom: 16,
            }}
          />
          <Title level={4} style={{ color: COLORS.ink }}>重置链接无效或已过期</Title>
          <Text style={{ color: COLORS.lightInk }}>
            该重置链接可能已过期或已被使用，请重新申请
          </Text>
          <div style={{ marginTop: 24 }}>
            <Link to="/forgot-password">
              <Button
                type="primary"
                style={{
                  marginRight: 12,
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                重新获取重置链接
              </Button>
            </Link>
            <Link to="/login">
              <Button
                style={{
                  borderRadius: 8,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
              >
                返回登录
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
          padding: '24px',
        }}
      >
        <Card
          style={{
            maxWidth: 440,
            textAlign: 'center',
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <CheckCircleOutlined
            style={{
              fontSize: 56,
              color: COLORS.bambooGreen,
              marginBottom: 16,
            }}
          />
          <Title level={4} style={{ color: COLORS.ink }}>密码已重置</Title>
          <Text style={{ color: COLORS.lightInk }}>
            您的新密码已设置成功，即将跳转到登录页面...
          </Text>
          <div style={{ marginTop: 24 }}>
            <Button
              type="primary"
              onClick={() => navigate('/login')}
              style={{
                borderRadius: 8,
                background: COLORS.teaBrown,
                borderColor: COLORS.teaBrown,
              }}
            >
              立即登录
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* 店铺品牌标识 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={storeName}
              style={{ maxHeight: 100, maxWidth: '100%', marginBottom: 8 }}
            />
          ) : (
            <Title
              level={2}
              style={{
                color: COLORS.ink,
                margin: 0,
                letterSpacing: '0.2em',
                fontWeight: 400,
                fontSize: 24,
              }}
            >
              {storeName}
            </Title>
          )}
          <div
            style={{
              width: 40,
              height: 2,
              background: COLORS.cinnabar,
              margin: '12px auto 0',
            }}
          />
        </div>

        <Card
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: 'none',
          }}
          bodyStyle={{ padding: '32px 32px 24px', background: COLORS.paper }}
        >
          <Title
            level={3}
            style={{
              textAlign: 'center',
              marginBottom: 12,
              color: COLORS.ink,
              fontWeight: 400,
            }}
          >
            重置密码
          </Title>
          <Text
            style={{
              display: 'block',
              textAlign: 'center',
              marginBottom: 24,
              color: COLORS.lightInk,
            }}
          >
            请输入您的新密码
          </Text>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            <Form.Item
              name="password"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>新密码</span>}
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请输入新密码"
                autoComplete="new-password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>确认密码</span>}
              rules={[
                { required: true, message: '请确认密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password
                prefix={<SafetyOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请再次输入新密码"
                autoComplete="new-password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                重置密码
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Link to="/login">
              <Text style={{ color: COLORS.lightInk }}>← 返回登录</Text>
            </Link>
          </div>
        </Card>

        {/* 返回首页 */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/">
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>← 返回首页</Text>
          </Link>
        </div>
      </div>
    </div>
  );
}
